import { useRef, useState } from 'react';

const ContactForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setSubmitting(true);
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value as string);
    });

    try {
      await fetch('https://readdy.ai/api/form/d7d8skhnoju8nk5a16t0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
      form.reset();
      setCharCount(0);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F7F8F9] rounded-2xl p-10 h-full flex flex-col">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-1 bg-[#E8521A] rounded-full" />
          <span
            className="text-[#E8521A] text-sm font-black tracking-widest uppercase"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Inquiry
          </span>
        </div>
        <h2
          className="text-4xl font-black text-[#1B2A4A]"
          style={{ fontFamily: "'Noto Sans KR', sans-serif", letterSpacing: '-0.03em' }}
        >
          견적 및 상담 문의
        </h2>
        <p
          className="text-[#1B2A4A]/55 text-base font-medium mt-2"
          style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
        >
          아래 양식을 작성해 주시면 빠르게 답변 드리겠습니다
        </p>
      </div>

      {submitted ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#E8521A]/10">
            <i className="ri-check-line text-[#E8521A] text-3xl" />
          </div>
          <p
            className="text-[#1B2A4A] text-xl font-black"
            style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
          >
            문의가 접수되었습니다
          </p>
          <p
            className="text-[#1B2A4A]/55 text-sm font-medium text-center"
            style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
          >
            담당자가 확인 후 빠른 시일 내에 연락드리겠습니다
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="mt-4 text-sm text-[#E8521A] font-bold underline underline-offset-2 cursor-pointer"
            style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
          >
            다시 문의하기
          </button>
        </div>
      ) : (
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          data-readdy-form
          className="flex flex-col gap-5 flex-1"
        >
          {/* Name + Company */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label
                className="text-base font-bold text-[#1B2A4A]"
                style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
              >
                성함 <span className="text-[#E8521A]">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                placeholder="홍길동"
                className="bg-white border border-[#1B2A4A]/15 rounded-xl px-4 py-3 text-base text-[#1B2A4A] placeholder-[#1B2A4A]/30 focus:outline-none focus:border-[#E8521A]/50 transition-colors"
                style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label
                className="text-base font-bold text-[#1B2A4A]"
                style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
              >
                소속 기관 / 회사
              </label>
              <input
                type="text"
                name="company"
                placeholder="(주)회사명"
                className="bg-white border border-[#1B2A4A]/15 rounded-xl px-4 py-3 text-base text-[#1B2A4A] placeholder-[#1B2A4A]/30 focus:outline-none focus:border-[#E8521A]/50 transition-colors"
                style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
              />
            </div>
          </div>

          {/* Phone + Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label
                className="text-base font-bold text-[#1B2A4A]"
                style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
              >
                연락처 <span className="text-[#E8521A]">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                required
                placeholder="010-0000-0000"
                className="bg-white border border-[#1B2A4A]/15 rounded-xl px-4 py-3 text-base text-[#1B2A4A] placeholder-[#1B2A4A]/30 focus:outline-none focus:border-[#E8521A]/50 transition-colors"
                style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label
                className="text-base font-bold text-[#1B2A4A]"
                style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
              >
                이메일 <span className="text-[#E8521A]">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                placeholder="example@email.com"
                className="bg-white border border-[#1B2A4A]/15 rounded-xl px-4 py-3 text-base text-[#1B2A4A] placeholder-[#1B2A4A]/30 focus:outline-none focus:border-[#E8521A]/50 transition-colors"
                style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
              />
            </div>
          </div>

          {/* Inquiry Type */}
          <div className="flex flex-col gap-2">
            <label
              className="text-base font-bold text-[#1B2A4A]"
              style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
            >
              문의 유형 <span className="text-[#E8521A]">*</span>
            </label>
            <select
              name="inquiry_type"
              required
              className="bg-white border border-[#1B2A4A]/15 rounded-xl px-4 py-3 text-base text-[#1B2A4A] focus:outline-none focus:border-[#E8521A]/50 transition-colors cursor-pointer"
              style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
            >
              <option value="">문의 유형을 선택해 주세요</option>
              <option value="지반조사 / 계측">지반조사 / 계측</option>
              <option value="스마트 계측">스마트 계측</option>
              <option value="정밀 안전진단">정밀 안전진단</option>
              <option value="AI 기반 지반 데이터 분석">AI 기반 지반 데이터 분석</option>
              <option value="견적 문의">견적 문의</option>
              <option value="기타">기타</option>
            </select>
          </div>

          {/* Message */}
          <div className="flex flex-col gap-2">
            <label
              className="text-base font-bold text-[#1B2A4A]"
              style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
            >
              문의 내용 <span className="text-[#E8521A]">*</span>
            </label>
            <textarea
              name="message"
              required
              rows={5}
              maxLength={500}
              placeholder="문의하실 내용을 자유롭게 작성해 주세요 (최대 500자)"
              onChange={(e) => setCharCount(e.target.value.length)}
              className="bg-white border border-[#1B2A4A]/15 rounded-xl px-4 py-3 text-base text-[#1B2A4A] placeholder-[#1B2A4A]/30 focus:outline-none focus:border-[#E8521A]/50 transition-colors resize-none"
              style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
            />
            <div className="flex justify-end">
              <span
                className={`text-sm font-medium ${charCount > 480 ? 'text-[#E8521A]' : 'text-[#1B2A4A]/40'}`}
                style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
              >
                {charCount} / 500
              </span>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || charCount > 500}
            className="w-full bg-[#E8521A] text-white font-bold text-xl py-4 rounded-xl hover:bg-[#d04515] transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                전송 중...
              </>
            ) : (
              <>
                문의 보내기
                <i className="ri-send-plane-line" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default ContactForm;
